/**
 * Thin glue around the Electron desktop shell bridge (`window.bunkerDesktop`,
 * mirrored in desktop/preload.js). No-ops in a browser — every method is
 * feature-detected.
 *
 * Used by the DM-toast layer to decide WHERE a notification goes: the in-app
 * HeroUI card when Bunker is the surface you're looking at, or the shell's
 * native always-on-top toast when it isn't (cispracs tab active / window
 * unfocused / minimised).
 */
import { useDataStore } from '@/stores/dataStore';
import { shellShowsBunker } from '@/lib/shellVisibility';

const bridge = typeof window !== 'undefined' ? window.bunkerDesktop : undefined;

/** True inside the Electron desktop shell (the `window.bunkerDesktop` bridge is
 *  injected by desktop/preload.js). False in a plain browser. Distinct from
 *  isNativeApp(), which only detects the Capacitor mobile shell. */
export function isDesktopShell(): boolean {
  return !!bridge;
}

// `null` until the shell tells us (or forever, in a browser / on an older shell
// that predates the tab-active signal — then we fall back to Page Visibility).
let bunkerTabActive: boolean | null = null;

bridge?.onTabActive?.((isBunkerActive) => {
  bunkerTabActive = isBunkerActive;
});

/**
 * A Desktop card was clicked — open its chat. The bridge is still called «open DM», but a card
 * is also a mention in a server channel (lib/dmToastQueue.tsx showMentionToast).
 */
export function openNotifiedChannel(channelId: unknown): boolean {
  if (typeof channelId !== 'string') return false;
  const store = useDataStore.getState();
  if (store.dmChannels[channelId]) {
    store.selectDirectChannel(channelId);
    return true;
  }
  for (const guild of Object.values(store.guilds)) {
    const channel = guild.channels.find((c) => c.id === channelId);
    if (!channel) continue;
    if (channel.type !== 'TEXT' && channel.type !== 'ANNOUNCE') return false;
    store.selectGuild(guild.id);
    store.selectChannel(channel.id);
    return true;
  }
  return false;
}

bridge?.onOpenDm?.(openNotifiedChannel);

/**
 * The shell's assistant asks to open a server (and maybe a text channel). Only a
 * server the user is already in, and only a text or announcement channel of it —
 * opening a voice channel this way could look like joining it.
 */
export function openChannelFromShell(target: unknown): boolean {
  if (!target || typeof target !== 'object') return false;
  const { guildId, channelId } = target as { guildId?: unknown; channelId?: unknown };
  if (typeof guildId !== 'string') return false;
  const store = useDataStore.getState();
  const guild = store.guilds[guildId];
  if (!guild) return false;
  store.selectGuild(guildId);
  const channel = typeof channelId === 'string' ? guild.channels.find((c) => c.id === channelId) : undefined;
  if (channel && (channel.type === 'TEXT' || channel.type === 'ANNOUNCE')) store.selectChannel(channel.id);
  return true;
}

bridge?.onOpenChannel?.(openChannelFromShell);

/** True when running inside a shell that can show native notification cards. */
export function canShowShellNotification(): boolean {
  return typeof bridge?.notify === 'function';
}

/**
 * Is Bunker's web content the surface the user is actually looking at right now?
 *  - browser / old shell: Page Visibility (the tab is foreground) — and, inside the
 *    platform's Chat frame, the shell showing that section (lib/shellVisibility.ts).
 *  - tabbed desktop shell: the Bunker tab is active AND the window is focused.
 */
export function isBunkerOnScreen(): boolean {
  if (typeof document === 'undefined') return false;
  const pageVisible = document.visibilityState === 'visible';
  if (bunkerTabActive === null) return pageVisible && shellShowsBunker();
  return bunkerTabActive && document.hasFocus();
}

/** Hand a DM card to the shell's native overlay (no-op without the bridge). */
export function showShellNotification(payload: {
  channelId: string;
  senderName: string;
  avatarUrl: string;
  groupName: string | null;
  preview: string;
}): void {
  bridge?.notify?.(payload);
}
